<script setup lang="ts">
    import BaseIcon from '@/components/BaseIcon.vue';
    import BaseInput from '@/components/BaseInput.vue';
    import BaseButton from '@/components/BaseButton.vue';
    import { useI18n } from 'vue-i18n';
    import { computed, useTemplateRef } from 'vue';

    const modelValue = defineModel<string>();

    const { t } = useI18n();
    defineEmits<{
        edit: [];
        delete: [];
        lock: [];
        click: [];
        focusin: [];
        focusout: [];
        duplicate: [];
    }>();

    interface Props {
        editable: boolean;
        deletable: boolean;
        lockable: boolean;
        duplicable: boolean;
        asButton: boolean;
        disabled?: boolean;
        placeholder?: string;
        warningMessage?: string;
        locked: boolean;
        deleteTitle?: string;
        duplicateTitle?: string;
        editTitle?: string;
    }

    const {
        deletable = false,
        editable = false,
        lockable = false,
        duplicable = false,
        disabled = false,
        asButton = false,
        locked = false,
        placeholder = undefined,
        deleteTitle = 'delete',
        duplicateTitle = 'duplicate',
        editTitle = 'edit',
    } = defineProps<Props>();

    const fieldWrapperRef = useTemplateRef('fieldWrapper');

    const defaultPlaceholder = computed<string>(() => {
        if (placeholder) {
            return placeholder;
        }
        return t('default_text_placeholder');
    });

    const warningMessageStyle = computed(() => {
        return {
            width: `${fieldWrapperRef.value?.clientWidth}px`,
        };
    });
</script>
<template>
    <div class="w-full">
        <div class="flex w-full flex-row items-center gap-4">
            <div
                ref="fieldWrapper"
                class="flex h-14 min-w-0 grow flex-row items-center justify-center gap-3 border border-border-primary bg-white p-3"
            >
                <span class="flex h-full flex-none flex-row items-center justify-center gap-3 bg-white">
                    <slot name="thumbnail" />
                </span>
                <BaseButton
                    v-if="asButton"
                    :title="modelValue || defaultPlaceholder"
                    variant="text"
                    :default-classes="false"
                    class="h-10 min-w-0 grow truncate border-transparent bg-white px-5 text-left text-sm text-text"
                    @click="$emit('click')"
                >
                    {{ modelValue || defaultPlaceholder }}
                </BaseButton>
                <BaseInput
                    v-else
                    v-model="modelValue"
                    :label="$t('element_input')"
                    :name="$t('element_input')"
                    :title="modelValue || defaultPlaceholder"
                    class="h-10 w-auto min-w-0 grow rounded-none border-transparent bg-white px-2 text-text placeholder:text-text focus:outline-none"
                    :disabled
                    :placeholder="defaultPlaceholder"
                    :show-label="false"
                    @focusin="$emit('focusin')"
                    @focusout="$emit('focusout')"
                />
                <span
                    v-if="warningMessage"
                    class="flex h-10 w-10 flex-none items-center justify-center bg-white"
                >
                    <span class="sr-only">{{ $t('warning') }}</span>
                    <span class="flex h-6 w-6 items-center justify-center rounded-full bg-alert-primary font-bold text-white"> ! </span>
                </span>
            </div>
            <BaseButton
                v-if="editable"
                :default-classes="false"
                :title="editTitle"
                variant="text"
                class="mx-2 h-4 w-4 flex-none border-none"
                @click="$emit('edit')"
            >
                <span class="sr-only">{{ $t('edit') }}</span>
                <BaseIcon
                    name="PencilIcon"
                    class="text-secondary"
                    type="outlined"
                />
            </BaseButton>
            <BaseButton
                :disabled="!duplicable"
                :default-classes="false"
                :title="duplicateTitle"
                variant="text"
                class="mx-2 h-4 w-4 flex-none border-none"
                @click="$emit('duplicate')"
            >
                <span class="sr-only">{{ $t('duplicate') }}</span>
                <BaseIcon
                    name="DocumentDuplicateIcon"
                    class="text-secondary"
                    type="outlined"
                />
            </BaseButton>
            <BaseButton
                v-if="deletable"
                :default-classes="false"
                :title="deleteTitle"
                variant="text"
                class="mx-2 h-4 w-4 flex-none border-none"
                @click="$emit('delete')"
            >
                <span class="sr-only">{{ $t('delete') }}</span>
                <BaseIcon
                    name="TrashIcon"
                    class="text-red"
                />
            </BaseButton>
            <BaseButton
                v-if="lockable"
                :default-classes="false"
                class="mx-2 h-4 w-4 flex-none border-none"
                variant="text"
                @click="$emit('lock')"
            >
                <span class="sr-only">{{ $t('lock') }}</span>
                <BaseIcon
                    name="LockClosedIcon"
                    :class="['text-black', locked && 'opacity-100', !locked && 'opacity-30']"
                />
            </BaseButton>
        </div>
        <div
            v-if="warningMessage"
            :style="warningMessageStyle"
            class="bg-alert-secondary p-2 text-alert-primary"
        >
            {{ warningMessage }}
        </div>
    </div>
</template>
